/**
 * urlState — the small amount of navigation state that belongs in the address
 * bar, and nothing else (Card PARTNER-LIST-REFRESH, 8 Oct 2026).
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * There is no router. `activePage` is state in <AuthenticatedApp/> initialised
 * to 'my-day' (src/App.js), and every sub-view below it — the open CRM section
 * tab, the open contact, the open partner org — is component state. So a
 * browser reload restores a SESSION but not a PLACE: the user lands on their
 * role's default page, whatever they were looking at.
 *
 * MEASURED IN THE DEMO (8 Oct 2026): a staff member on the phone with a new
 * consultation reloaded to see a record that had just been created, and was
 * thrown out of the contact they were on. That is the failure this module
 * closes. REQUIREMENTS.md already documents the My Day landing as intended
 * behaviour for a FRESH sign-in; it was never meant to be what a refresh does.
 *
 * ── ⛔⛔ WHAT MAY GO IN A URL, AND WHY THE ALLOWLIST IS A LIST OF KEYS ───────
 *
 * A URL is the least private place in the product. It reaches the address bar,
 * the browser history, a bookmark, a screenshot, session restore, and the
 * Referer header on the next outbound request. src/components/CrmFilterBar
 * already reasons this through for CRM free-text search and keeps it in
 * sessionStorage for exactly that reason — "a CRM search is routinely a
 * patient/lead NAME".
 *
 * So this module does not offer a general "put this in the URL" function. It
 * offers a FIXED SET OF KEYS, each with a validator that accepts only an
 * opaque identifier shape. A name, a phone number, an email address or a note
 * body cannot be written through this API because there is no key that would
 * accept one — the restriction is structural, not a convention a future caller
 * has to remember.
 *
 * ⚠ An id is not nothing. A URL carrying `contact=13727` tells a shoulder-
 * surfer that a record exists and is open. That is the same disclosure the
 * on-screen title already makes to anyone looking at the monitor, and is the
 * trade the card accepts in exchange for not losing a caller's place. A NAME
 * would be a different disclosure entirely, which is why it cannot be written.
 */

import { stampOwner } from './loginNonce';

/**
 * The only keys this module will read or write.
 *
 * `slug`   — a short lowercase route/tab identifier from a static list in the
 *            source (e.g. 'alliancepartners', 'contacts', 'profile'). Never
 *            user-supplied text.
 * `id`     — a positive integer identifier, as a string. Partner org ids are
 *            BIGSERIAL and arrive from pg as strings, so this is deliberately
 *            a string comparison and never Number().
 */
const KEYS = Object.freeze({
  page:     'slug',  // which top-level page (App.js `pages` map key)
  tab:      'slug',  // CRM section tab (CRMPage GROUPS id)
  contact:  'id',    // open CRM contact
  ctab:     'slug',  // tab within the open contact
  org:      'id',    // open alliance partner organization
  // ── Added by Card PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026 ──────────────────
  // The selected CRM pipeline and the stage column being read. Both name a
  // CONFIGURATION ROW (crm_pipelines.id / crm_pipeline_stages.id) and not a
  // person, so they are strictly less disclosive than `contact` — which the
  // reasoning above already accepted. They are here rather than in storage
  // alone so that a reload returns to the same board and the same column, the
  // behaviour Card PARTNER-LIST-REFRESH established for the contact record.
  pipeline: 'id',    // selected CRM pipeline (crm_pipelines.id)
  stage:    'id',    // stage column being read (crm_pipeline_stages.id)
  // ── Added by Card P185, 31 Oct 2026 ─────────────────────────────────────
  // The CEO dashboard's custom reporting window. A CALENDAR DATE discloses
  // nothing about any person — it is the same fact the period pills already
  // put on screen in words, and strictly less disclosive than `contact`,
  // which the reasoning above already accepted. They are here so a chosen
  // range survives a reload and the browser Back button, which is the whole
  // of the card's "the range must come back" requirement.
  // ⚠ A NEW KIND, not reused: a date is neither a slug nor an id, and
  // validating it as either would accept '20261301' or reject '2026-09-01'.
  from:     'date',  // inclusive start, YYYY-MM-DD, business time
  to:       'date',  // inclusive end,   YYYY-MM-DD, business time
});

const SLUG_RE = /^[a-z][a-z0-9_-]{0,39}$/;
const ID_RE = /^[1-9][0-9]{0,18}$/;
/*
 * ⚠ SHAPE ONLY — this says "looks like a calendar date", not "is one".
 * 2026-02-30 passes here and is refused later by customRange(), which checks
 * the round trip. Two gates on purpose: this one keeps nonsense out of the
 * URL, that one keeps an impossible date out of a query.
 */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** @returns {boolean} true when `value` is a legal value for `key`. */
export function isAllowed(key, value) {
  const kind = KEYS[key];
  if (!kind) return false;
  const s = String(value == null ? '' : value);
  if (s === '') return false;
  if (kind === 'id') return ID_RE.test(s);
  if (kind === 'date') return DATE_RE.test(s);
  return SLUG_RE.test(s);
}

/**
 * Read one allowlisted key. Returns '' for a missing key, an unknown key, or a
 * value that fails its validator — so a hand-edited or stale URL degrades to
 * "no opinion" rather than driving the app somewhere invalid.
 */
export function readParam(key) {
  if (!KEYS[key]) return '';
  try {
    const raw = new URLSearchParams(window.location.search).get(key);
    if (raw == null) return '';
    return isAllowed(key, raw) ? String(raw) : '';
  } catch { return ''; }
}

/** Read every allowlisted key at once. */
export function readAll() {
  const out = {};
  for (const key of Object.keys(KEYS)) out[key] = readParam(key);
  return out;
}

/**
 * Write allowlisted keys, dropping any whose value is empty/null.
 *
 * ⛔ replaceState, never pushState — from THIS module. Navigating inside the
 * app already pushes a NavHistory entry (src/lib/NavHistory.js is the single
 * push choke point); also pushing browser history from here would make the
 * hardware/browser Back button walk a second, differently-shaped stack and
 * fight the in-app one. (src/lib/browserBackStep.js does push, deliberately and
 * in exactly one place — see its header for the invariants that keep its one
 * entry a strict subset of the in-app stack.)
 *
 * ⚠ A disallowed key or value is DROPPED SILENTLY rather than throwing. This is
 * presentation state: a future caller passing something unexpected must not be
 * able to break the page the user is on. The guard that matters — "a name can
 * never reach the address bar" — holds either way, and a unit test drives it.
 */
export function writeParams(params) {
  try {
    const p = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(params || {})) {
      if (!KEYS[k]) continue;
      if (v == null || v === '' || !isAllowed(k, v)) p.delete(k);
      else p.set(k, String(v));
    }
    const qs = p.toString();
    /*
     * ⛔⛔ EVERY ENTRY THIS APP WRITES CARRIES ITS OWNER — review round 5.
     *
     * Two things happen here, and they are different:
     *
     *  1. THE EXISTING state IS CARRIED, NOT NULLED. This used to pass `null`,
     *     which REPLACES the entry's state object and would wipe anything
     *     another part of the app had put there — today, the browser-back
     *     step's marker, which this writer fires on the very transition that
     *     writes it.
     *
     *  2. THE OWNER IS STAMPED. `src/lib/loginNonce.js` explains why at
     *     length; the short version is that rounds 3 and 4 secured the entry
     *     we stand on and the one beneath it, and MEASURED in Chrome and
     *     WebKit, the rest of the stack still carried one user's open record
     *     into the next user's browser by way of the Forward button. An entry
     *     nobody has stamped stays unowned on purpose — that is a pasted deep
     *     link or a bookmark, and it must keep working.
     */
    const carried = (() => { try { return window.history.state; } catch { return null; } })();
    window.history.replaceState(stampOwner(carried), '', qs ? `?${qs}` : window.location.pathname);
  } catch { /* history unavailable (very old browser, or a test env) */ }
}

export const ALLOWED_KEYS = Object.freeze(Object.keys(KEYS));
